import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';

import moment from 'moment';
import store from '../../../utilities/store';
import { StateManager } from '../../../utilities/stateManager';
import { useSelectMenuProps } from '../../../utilities/ModalStackContext';
import SendCreditAppDialog from '../../SendCreditAppDialog';

// Send a deal's credit apps to lenders from the car itself. The buyer and
// co-buyer already sit on the deal, so their most recent credit apps are pulled
// automatically — no linking a car or a co-applicant by hand first.
export default function SendToLendersDialog({ open, onClose, car = {} }) {
  const stock = car.stock;
  const buyerId = car.buyer?.id;
  const cobuyerId = car.cobuyer?.id;

  const [loading, setLoading] = React.useState(false);
  const [buyerApps, setBuyerApps] = React.useState([]);
  const [coApps, setCoApps] = React.useState([]);
  const [buyerAppId, setBuyerAppId] = React.useState('');
  const [coAppId, setCoAppId] = React.useState('');

  React.useEffect(() => {
    if (!open) return undefined;
    let cancelled = false;
    setLoading(true);

    Promise.all([
      buyerId ? store.getCreditAppsForCustomer(buyerId) : Promise.resolve([]),
      cobuyerId ? store.getCreditAppsForCustomer(cobuyerId) : Promise.resolve([]),
    ])
      .then(([buyerResults, coResults]) => {
        if (cancelled) return;
        setBuyerApps(buyerResults);
        setCoApps(coResults);
        setBuyerAppId(buyerResults[0]?.id || '');
        // Prefer whatever the buyer's app is already linked to; otherwise the
        // co-buyer's most recent app.
        const linked = buyerResults[0]?.co_applicant;
        setCoAppId(coResults.find(a => a.id === linked)?.id || coResults[0]?.id || '');
      })
      .catch(() => {
        if (!cancelled) StateManager.setAlertAndOpen('Could not load credit apps', 'error');
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [open, buyerId, cobuyerId]);

  const buyerApp = buyerApps.find(a => a.id === buyerAppId) || null;
  const carTitle = [car.year, car.make, car.model].filter(Boolean).join(' ');

  // Runs before the preview is built: point the app at this car and this
  // co-applicant so the packet carries the right bill of sale and co-buyer.
  const prepare = async () => {
    if (!buyerAppId || !buyerApp) throw new Error('Select a credit app for the buyer');

    if (stock && buyerApp.stock !== stock) {
      await store.linkCreditAppToCar(buyerAppId, {
        stock,
        car_title: carTitle,
        car_thumbnail: car.thumbnail || '',
      });
    }

    const currentCoApp = buyerApp.co_applicant || '';
    if (coAppId && coAppId !== currentCoApp) await store.linkCoApplicant(buyerAppId, coAppId);
    else if (!coAppId && currentCoApp) await store.unlinkCoApplicant(buyerAppId);

    setBuyerApps(prev => prev.map(a => (
      a.id === buyerAppId
        ? { ...a, stock, car_title: carTitle, car_thumbnail: car.thumbnail || '', co_applicant: coAppId || null }
        : a
    )));
  };

  return (
    <SendCreditAppDialog
      open={open}
      onClose={onClose}
      creditAppId={buyerAppId || null}
      title="Send Credit App to Lenders"
      prepare={prepare}
      headerContent={
        <ApplicantPicker
          loading={loading}
          buyer={car.buyer}
          cobuyer={car.cobuyer}
          buyerApps={buyerApps}
          coApps={coApps}
          buyerAppId={buyerAppId}
          coAppId={coAppId}
          onBuyerAppChange={setBuyerAppId}
          onCoAppChange={setCoAppId}
          stock={stock}
          carTitle={carTitle}
        />
      }
    />
  );
}

// Rendered inside the dialog so the Select menus pick up the dialog's z-index.
function ApplicantPicker({
  loading, buyer, cobuyer, buyerApps, coApps,
  buyerAppId, coAppId, onBuyerAppChange, onCoAppChange, stock, carTitle,
}) {
  const selectMenuProps = useSelectMenuProps();

  if (loading) {
    return (
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, py: 2 }}>
        <CircularProgress size={18} />
        <Typography variant="body2" color="text.secondary">Loading credit apps…</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 2 }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1 }}>Applicants</Typography>

      {!buyer?.id ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          No buyer on this deal. Assign one on the Sales tab first.
        </Alert>
      ) : buyerApps.length === 0 ? (
        <Alert severity="error" sx={{ mb: 1 }}>
          No credit app on file for {buyer.display_name || 'the buyer'}. Link one from the
          customer's Credit Apps tab, or have them submit an application.
        </Alert>
      ) : (
        <AppSelect
          label={`Buyer — ${buyer.display_name || ''}`.trim()}
          apps={buyerApps}
          value={buyerAppId}
          onChange={onBuyerAppChange}
          menuProps={selectMenuProps}
        />
      )}

      {cobuyer?.id && (
        coApps.length === 0 ? (
          <Alert severity="warning" sx={{ mt: 1 }}>
            No credit app on file for co-buyer {cobuyer.display_name || ''}. The packet goes out
            with the buyer only.
          </Alert>
        ) : (
          <Box sx={{ mt: 1.5 }}>
            <AppSelect
              label={`Co-buyer — ${cobuyer.display_name || ''}`.trim()}
              apps={coApps}
              value={coAppId}
              onChange={onCoAppChange}
              menuProps={selectMenuProps}
              allowNone
            />
          </Box>
        )
      )}

      {stock && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.5 }}>
          Sent against stock #{stock}{carTitle ? ` · ${carTitle}` : ''} — the app is linked to this
          car and the bill of sale comes from this deal.
        </Typography>
      )}

      <Divider sx={{ mt: 2 }} />
    </Box>
  );
}

function AppSelect({ label, apps, value, onChange, menuProps, allowNone }) {
  return (
    <FormControl fullWidth size="small">
      <InputLabel>{label}</InputLabel>
      <Select label={label} value={value} MenuProps={menuProps} onChange={e => onChange(e.target.value)}>
        {allowNone && <MenuItem value=""><em>None</em></MenuItem>}
        {apps.map(app => (
          <MenuItem key={app.id} value={app.id}>{appLabel(app)}</MenuItem>
        ))}
      </Select>
    </FormControl>
  );
}

const appLabel = (app) => {
  const submitted = app.time ? moment(app.time, 'YYYY-MM-DD HH:mm:ss').format('MMM D, YYYY') : 'No date';
  const name = `${app.first_name || ''} ${app.last_name || ''}`.trim();
  const status = app.status ? ` · ${app.status}` : '';
  return `${name || 'Credit app'} — ${submitted}${status}`;
};
